import { toaster } from "@repo/ui3/components";
import { WORKSPACE_ID_HEADER } from "@repo/workspace-route";
import {
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useAuthorizedUser } from "src/features/auth/useAuthorizedUser";
import {
  QuestionnaireResponseExportContext,
  type QuestionnaireResponseExportContextValue,
  type QuestionnaireResponseExportState,
  type QuestionnaireResponseExportTask,
} from "./context";

type Props = {
  children: ReactNode;
};

export const QuestionnaireResponseExportProvider: FC<Props> = ({
  children,
}) => {
  const { currentWorkspace } = useAuthorizedUser();
  const [state, setState] = useState<QuestionnaireResponseExportState>({
    status: "idle",
    currentTask: null,
    error: null,
  });

  const startExport = useCallback<
    QuestionnaireResponseExportContextValue["startExport"]
  >(
    async (taskInput) => {
      if (state.status === "running") {
        toaster.create({
          type: "warning",
          title: "CSVを生成中です",
          description: "現在のダウンロードが完了してから再実行してください。",
        });
        return;
      }

      const task: QuestionnaireResponseExportTask = {
        ...taskInput,
        startedAt: new Date(),
      };
      setState({ status: "running", currentTask: task, error: null });

      try {
        const response = await fetch(
          buildExportUrl(task),
          buildExportRequestInit(currentWorkspace.id),
        );

        if (!response.ok) {
          throw new Error(await buildErrorMessage(response));
        }

        const blob = await response.blob();
        downloadBlob(
          blob,
          extractFilename(response) ?? buildFallbackFilename(task),
        );
        setState({ status: "success", currentTask: task, error: null });
        toaster.create({
          type: "success",
          title: "CSVをダウンロードしました",
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "CSVの生成に失敗しました。";
        setState({ status: "error", currentTask: task, error: message });
        toaster.create({
          type: "error",
          title: "CSVの生成に失敗しました",
          description: message,
        });
      }
    },
    [currentWorkspace.id, state.status],
  );

  const clearError = useCallback(() => {
    setState((current) => {
      if (current.status !== "error") return current;
      return { status: "idle", currentTask: null, error: null };
    });
  }, []);

  const value = useMemo(
    () => ({ state, startExport, clearError }),
    [state, startExport, clearError],
  );

  return (
    <QuestionnaireResponseExportContext.Provider value={value}>
      {children}
    </QuestionnaireResponseExportContext.Provider>
  );
};

export const useQuestionnaireResponseExport = () => {
  const context = useContext(QuestionnaireResponseExportContext);
  if (!context) {
    throw new Error("QuestionnaireResponseExportContext not found");
  }
  return context;
};

export const buildExportUrl = (task: QuestionnaireResponseExportTask) => {
  const graphqlEndpoint = process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT;
  if (!graphqlEndpoint) {
    throw new Error("GraphQL endpoint is not configured.");
  }

  const url = new URL(
    "/karte/questionnaire_response_exports.csv",
    graphqlEndpoint,
  );
  url.searchParams.set("questionnaire_id", task.questionnaireId);
  url.searchParams.set("questionnaire_schema_id", task.questionnaireSchemaId);
  url.searchParams.set("created_at_from", task.createdAtFrom);
  url.searchParams.set("created_at_to", task.createdAtTo);
  return url;
};

export const buildExportRequestInit = (workspaceId: string): RequestInit => ({
  credentials: "include",
  headers: {
    [WORKSPACE_ID_HEADER]: workspaceId,
  },
});

export const buildErrorMessage = async (response: Response) => {
  const fallback = `CSVの生成に失敗しました。(${response.status})`;

  try {
    const body = await response.json();
    if (body.error === "too_many_records") {
      return `対象件数が上限を超えています。期間または世代を絞り込んでください。上限: ${body.limit.toLocaleString()}件`;
    }
    if (body.error === "forbidden") {
      return "データエクスポートを実行する権限がありません。";
    }
    return fallback;
  } catch {
    return fallback;
  }
};

export const extractFilename = (response: Response) => {
  const disposition = response.headers.get("Content-Disposition");
  if (!disposition) return null;

  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/);
  if (encoded?.[1]) {
    return decodeURIComponent(encoded[1]);
  }

  const plain = disposition.match(/filename="?([^";]+)"?/);
  return plain?.[1] ?? null;
};

export const buildFallbackFilename = (
  task: QuestionnaireResponseExportTask,
) => {
  const from = task.createdAtFrom.slice(0, 10).replaceAll("-", "");
  const to = task.createdAtTo.slice(0, 10).replaceAll("-", "");
  const title = sanitizeFilenamePart(task.questionnaireTitle).slice(0, 40);
  const schemaLabel = sanitizeFilenamePart(task.schemaLabel);
  return `speria_${title}_${schemaLabel}_${from}-${to}.csv`;
};

const sanitizeFilenamePart = (value: string) =>
  value.replaceAll(/[\\/:*?"<>|]/g, "_").replaceAll(/\s+/g, "");

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
